import {
    Bold,
    Code,
    Italic,
    Link,
    List,
    ListOrdered,
    Quote,
    Strikethrough,
    Underline,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface FormattingToolbarProps {
    textareaRef: React.RefObject<HTMLTextAreaElement>;
    onFormat: (prefix: string, suffix: string, multiline?: "bullet" | "numbered" | "quote") => void;
    className?: string;
}

interface FormatButton {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    prefix: string;
    suffix: string;
    shortcut?: string;
    multiline?: "bullet" | "numbered" | "quote";
}

/**
 * Compact horizontal formatting toolbar. Inserts markdown syntax around
 * the currently selected text in the companion textarea.
 *
 * Props:
 *   `textareaRef` — ref to the <textarea> element this toolbar controls.
 *   `onFormat`    — called with the markdown prefix/suffix to apply; the
 *                   toolbar does NOT mutate textarea.value directly since
 *                   the textarea value is controlled from the parent.
 */
export function FormattingToolbar({ textareaRef, onFormat, className }: FormattingToolbarProps) {
    const applyFormat = (btn: FormatButton) => {
        const ta = textareaRef.current;
        if (!ta) return;
        const { selectionStart: start, selectionEnd: end, value } = ta;
        const selected = value.slice(start, end);

        if (btn.multiline && selected) {
            // Multi-line wrapping: prepend each line
            const lines = selected.split("\n");
            let formatted: string;
            if (btn.multiline === "bullet") {
                formatted = lines.map((l) => `- ${l}`).join("\n");
            } else if (btn.multiline === "numbered") {
                formatted = lines.map((l, i) => `${i + 1}. ${l}`).join("\n");
            } else {
                // quote
                formatted = lines.map((l) => `> ${l}`).join("\n");
            }
            onFormat("", "", btn.multiline);
            // Pass the formatted text back through a synthetic call
            const syntheticOnFormat = () => {
                // Build the new value
                const next = value.slice(0, start) + formatted + value.slice(end);
                ta.value = next;
                ta.dispatchEvent(new Event("input", { bubbles: true }));
                requestAnimationFrame(() => {
                    ta.focus();
                    ta.setSelectionRange(start, start + formatted.length);
                });
            };
            syntheticOnFormat();
            return;
        }

        onFormat(btn.prefix, btn.suffix);
    };

    const BUTTONS: FormatButton[][] = [
        [
            { icon: Bold,          label: "Bold (Ctrl+B)",        prefix: "**", suffix: "**", shortcut: "B" },
            { icon: Italic,        label: "Italic (Ctrl+I)",       prefix: "_",  suffix: "_",  shortcut: "I" },
            { icon: Underline,     label: "Underline",             prefix: "<u>", suffix: "</u>" },
            { icon: Strikethrough, label: "Strikethrough",         prefix: "~~", suffix: "~~" },
        ],
        [
            { icon: Code,          label: "Inline code",           prefix: "`",  suffix: "`" },
        ],
        [
            { icon: List,          label: "Bullet list",           prefix: "- ", suffix: "", multiline: "bullet" },
            { icon: ListOrdered,   label: "Numbered list",         prefix: "1. ", suffix: "", multiline: "numbered" },
            { icon: Quote,         label: "Blockquote",            prefix: "> ", suffix: "", multiline: "quote" },
        ],
        [
            { icon: Link,          label: "Link",                  prefix: "[", suffix: "](url)" },
        ],
    ];

    return (
        <div
            className={cn("flex items-center gap-0.5 flex-wrap", className)}
            role="toolbar"
            aria-label="Text formatting"
        >
            {BUTTONS.map((group, gi) => (
                <span key={gi} className="flex items-center">
                    {gi > 0 ? (
                        <span className="mx-0.5 h-4 w-px bg-border" aria-hidden />
                    ) : null}
                    {group.map((btn) => (
                        <button
                            key={btn.label}
                            type="button"
                            title={btn.label}
                            aria-label={btn.label}
                            onClick={() => applyFormat(btn)}
                            className="flex h-7 w-7 items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                        >
                            <btn.icon className="h-3.5 w-3.5" />
                        </button>
                    ))}
                </span>
            ))}
        </div>
    );
}
